import { API_BASE_URL } from '../constants/config';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.API_BASE_URL = API_BASE_URL;
    // Client hint for the priority-slots gate on getLists. null = unknown (omit the
    // query param and let the backend fall back to reading the stored preference).
    this.priorityMode = null;
  }

  // Set the priority-mode client hint (from user.priority_mode_enabled). When set,
  // getLists sends ?priority_mode=<value> so the backend can skip the pref read
  // (and, when false, skip all slot work).
  setPriorityMode(enabled) {
    this.priorityMode = enabled;
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async handleResponse(response) {
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        window.location.href = '/login';
      }
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new Error(error.detail || 'An error occurred');
    }
    return response.json();
  }

  // Auth
  async login(name, email) {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email }),
    });
    const data = await this.handleResponse(response);
    this.setToken(data.access_token);
    return data;
  }

  async googleLogin(idToken) {
    const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
    });
    const data = await this.handleResponse(response);
    this.setToken(data.access_token);
    return data;
  }

  // Lists
  async getLists() {
    const query = this.priorityMode === null
      ? ''
      : `?priority_mode=${this.priorityMode ? 'true' : 'false'}`;
    const response = await fetch(`${API_BASE_URL}/api/lists${query}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Lightweight list metadata (names, sharing, task counts) for the Account page.
  // Much faster than getLists -- no per-task payload.
  async getListsMetadata() {
    const response = await fetch(`${API_BASE_URL}/api/lists/metadata`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createList(name) {
    const response = await fetch(`${API_BASE_URL}/api/lists`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name }),
    });
    return this.handleResponse(response);
  }

  async updateList(listId, name) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name }),
    });
    return this.handleResponse(response);
  }

  async deleteList(listId) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async shareList(listId, email) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/share`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ user_email: email }),
    });
    return this.handleResponse(response);
  }

  async unshareList(listId, userId) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/share/${userId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Tasks
  async addTask(listId, text, { subTasks, notes } = {}) {
    const body = { text };
    if (subTasks) body.sub_tasks = subTasks;
    if (notes) body.notes = notes;
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/tasks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return this.handleResponse(response);
  }

  async buildList(listId, description, listName = null) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/build`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ description, list_name: listName }),
    });
    return this.handleResponse(response);
  }

  async reorderTasks(listId, taskIds) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/reorder`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(taskIds),
    });
    return this.handleResponse(response);
  }

  async reorderLists(listIds) {
    const response = await fetch(`${API_BASE_URL}/api/lists/reorder`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(listIds),
    });
    return this.handleResponse(response);
  }

  async toggleTask(taskId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/toggle`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async updateTask(taskId, text) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ text }),
    });
    return this.handleResponse(response);
  }

  async updateTaskMetadata(taskId, metadata) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/metadata`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ metadata }),
    });
    return this.handleResponse(response);
  }

  async assignTask(taskId, assignedToUserId) {
    // Pass null to unassign
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/assign`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ assigned_to_user_id: assignedToUserId ?? null }),
    });
    return this.handleResponse(response);
  }

  async makeTaskRecurring(taskId, recurringConfig) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/make-recurring`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(recurringConfig),
    });
    return this.handleResponse(response);
  }

  async deleteTask(taskId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async deleteMultipleTasks(taskIds) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/bulk-delete`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(taskIds),
    });
    return this.handleResponse(response);
  }

  async promoteToMain(taskId, useTomorrow = false) {
    const url = new URL(`${API_BASE_URL}/api/tasks/${taskId}/promote`);
    if (useTomorrow) {
      url.searchParams.append('use_tomorrow', 'true');
    }
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async demoteFromMain(taskId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/demote`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async snoozeTask(taskId) {
    // Snooze: remove from main list and add to tomorrow list
    // First demote from main, then promote to tomorrow
    await this.demoteFromMain(taskId);
    return this.promoteToMain(taskId, true);
  }

  // Sub-tasks
  async getSubTasksForList(listId) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/subtasks`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getSubTasks(taskId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/subtasks`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createSubTask(taskId, text) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/subtasks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ text }),
    });
    return this.handleResponse(response);
  }

  async updateSubTask(subTaskId, updates) {
    const response = await fetch(`${API_BASE_URL}/api/subtasks/${subTaskId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    return this.handleResponse(response);
  }

  async deleteSubTask(subTaskId) {
    const response = await fetch(`${API_BASE_URL}/api/subtasks/${subTaskId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Notes
  async getNotes(taskId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/notes`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createNote(taskId, text) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/notes`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ text }),
    });
    return this.handleResponse(response);
  }

  async updateNote(noteId, text) {
    const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ text }),
    });
    return this.handleResponse(response);
  }

  async deleteNote(noteId) {
    const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // AI Enhancement
  async aiEnhanceTask(taskId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/ai-enhance`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Recurring Tasks
  async getRecurringTasks() {
    const response = await fetch(`${API_BASE_URL}/api/recurring-tasks`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createRecurringTask(recurringTask) {
    const response = await fetch(`${API_BASE_URL}/api/recurring-tasks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(recurringTask),
    });
    return this.handleResponse(response);
  }

  async deleteRecurringTask(recurringTaskId) {
    const response = await fetch(`${API_BASE_URL}/api/recurring-tasks/${recurringTaskId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Move task to another list
  async moveTaskToList(taskId, targetListId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/move`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ target_list_id: targetListId }),
    });
    return this.handleResponse(response);
  }

  // Location Tasks
  async getLocationTasks() {
    const response = await fetch(`${API_BASE_URL}/api/location-tasks`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createLocationTask(locationTask) {
    const response = await fetch(`${API_BASE_URL}/api/location-tasks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(locationTask),
    });
    return this.handleResponse(response);
  }

  async deleteLocationTask(locationTaskId) {
    const response = await fetch(`${API_BASE_URL}/api/location-tasks/${locationTaskId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Priority slots
  async getPrioritySlots() {
    const response = await fetch(`${API_BASE_URL}/api/priority-slots`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createPrioritySlot(listId) {
    const response = await fetch(`${API_BASE_URL}/api/priority-slots`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ list_id: listId }),
    });
    return this.handleResponse(response);
  }

  async deletePrioritySlot(slotId) {
    const response = await fetch(`${API_BASE_URL}/api/priority-slots/${slotId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async reorderPrioritySlots(slotIds) {
    const response = await fetch(`${API_BASE_URL}/api/priority-slots/reorder`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ slot_ids: slotIds }),
    });
    return this.handleResponse(response);
  }

  async fulfillPrioritySlot(slotId) {
    const response = await fetch(`${API_BASE_URL}/api/priority-slots/${slotId}/fulfill`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async geocodeCity(cityName) {
    const response = await fetch(`${API_BASE_URL}/api/location-tasks/geocode`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ city_name: cityName }),
    });
    return this.handleResponse(response);
  }

  async checkLocationAndCreateTasks(latitude, longitude) {
    const response = await fetch(`${API_BASE_URL}/api/location-tasks/check`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ latitude, longitude }),
    });
    return this.handleResponse(response);
  }

  async reportLocation(latitude, longitude) {
    const response = await fetch(`${API_BASE_URL}/api/location/report`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ latitude, longitude }),
    });
    return this.handleResponse(response);
  }

  // User Preferences
  async getUserPreferences() {
    const response = await fetch(`${API_BASE_URL}/api/user/preferences`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async updateUserPreferences(preferences) {
    const response = await fetch(`${API_BASE_URL}/api/user/preferences`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(preferences),
    });
    return this.handleResponse(response);
  }

  // List view preferences
  async getListViewPreferences() {
    const response = await fetch(`${API_BASE_URL}/api/user/list-view-preferences`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async setListViewPreference(listId, viewMode) {
    const response = await fetch(`${API_BASE_URL}/api/user/list-view-preferences/${listId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ view_mode: viewMode }),
    });
    return this.handleResponse(response);
  }

  // Analytics
  async getAverageTimeToClose(days = 30) {
    const response = await fetch(`${API_BASE_URL}/api/analytics/average-time-to-close?days=${days}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getTasksClosedOverTime(days = 30) {
    const response = await fetch(`${API_BASE_URL}/api/analytics/tasks-closed?days=${days}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getPopularListsOverTime(days = 30) {
    const response = await fetch(`${API_BASE_URL}/api/analytics/popular-lists?days=${days}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getTaskAgeDistribution() {
    const response = await fetch(`${API_BASE_URL}/api/analytics/task-age-distribution`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Achievement Metrics
  async getAchievementMetrics(listId) {
    const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/achievement-metrics`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Completion Grid
  async getTaskCompletionGrid(taskId) {
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/completion-grid`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Browser Extension Download
  async downloadExtension() {
    const response = await fetch(`${API_BASE_URL}/api/extension/download`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        window.location.href = '/login';
      }
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new Error(error.detail || 'An error occurred');
    }
    return response.blob();
  }
}

export const api = new ApiService();

